/**
 * Typed schemas for the document layer.
 *
 * Goal: keep the rest of the UI ignorant of *where* documents come from
 * (bundled samples, public S3, future authenticated backend API, GitHub,
 * Confluence, …). Every provider must produce these shapes, and the UI
 * consumes only these shapes.
 *
 * The schemas are intentionally minimal in v0 — we add fields cautiously as
 * concrete needs (auth, versioning, plugins, multi-tenant) appear.
 */

/** A node in the navigation tree. Either a folder ("group") or a leaf
 * ("document"). Folders may carry an `indexDocumentId` so a click on the
 * folder itself can open a default child (e.g. README.md). */
export type NavigationNode = NavigationGroup | NavigationDocument;

export interface NavigationGroup {
  readonly kind: "group";
  /** Stable, URL-safe identifier (unique within the tree). */
  readonly id: string;
  /** Display label shown in the sidebar. */
  readonly label: string;
  /** Optional human-friendly description shown on hover / metadata. */
  readonly description?: string;
  /** Children, in display order. */
  readonly children: readonly NavigationNode[];
  /** Optional id of a child document to open when the group itself is clicked. */
  readonly indexDocumentId?: string;
}

export interface NavigationDocument {
  readonly kind: "document";
  /** Stable, URL-safe identifier (unique within the tree). */
  readonly id: string;
  /** Display label shown in the sidebar. */
  readonly label: string;
  /** Optional one-line summary. */
  readonly description?: string;
  /** Provider-specific opaque locator (S3 key, API path, …). The UI never inspects it. */
  readonly source: DocumentSource;
}

/** Opaque, provider-specific locator. Tagged so future providers (api,
 * github, confluence, …) can plug in without leaking S3 specifics. */
export type DocumentSource =
  | { readonly type: "static"; readonly path: string }
  | { readonly type: "http"; readonly url: string }
  | { readonly type: "api"; readonly endpoint: string };

/** The full navigation tree, plus top-level metadata. */
export interface DocumentTree {
  readonly version: 1;
  readonly generatedAt?: string;
  readonly title?: string;
  readonly description?: string;
  readonly root: readonly NavigationNode[];
}

/** Optional metadata block surfaced in the document header. Mirrors fields
 * we know the CloudoX generator already produces (see report-metadata.json),
 * but every field is optional so older / external reports still render. */
export interface DocumentMetadata {
  readonly title?: string;
  readonly description?: string;
  readonly generatedAt?: string;
  readonly provider?: string;
  readonly model?: string;
  readonly organization?: string;
  readonly source?: string;
  readonly tags?: readonly string[];
  /** Free-form key/value strings the UI renders defensively. */
  readonly extra?: Readonly<Record<string, string>>;
}

/** A loaded document, ready to render. */
export interface Document {
  readonly id: string;
  readonly label: string;
  readonly markdown: string;
  readonly metadata: DocumentMetadata;
}

/** Tagged error type so the UI can show specific empty / not-found / failure
 * states without parsing error message strings. */
export type DocumentError =
  | { readonly code: "not_found"; readonly message: string; readonly cause?: unknown }
  | { readonly code: "invalid_document"; readonly message: string; readonly cause?: unknown }
  | { readonly code: "unauthorized"; readonly message: string; readonly cause?: unknown }
  | { readonly code: "network_error"; readonly message: string; readonly cause?: unknown }
  | { readonly code: "unknown"; readonly message: string; readonly cause?: unknown };

export class DocumentProviderError extends Error {
  readonly code: DocumentError["code"];
  override readonly cause?: unknown;

  constructor(error: DocumentError) {
    super(error.message);
    this.name = "DocumentProviderError";
    this.code = error.code;
    if (error.cause !== undefined) {
      this.cause = error.cause;
    }
  }
}

/** The abstraction the UI depends on. Anything that satisfies this contract
 * can replace S3 — future backend API, in-memory store, GitHub adapter, … */
export interface DocumentProvider {
  /** Human-readable id used in logs / metadata. */
  readonly id: string;
  /** Load the navigation tree. Cached by the UI; providers should be idempotent. */
  loadTree(signal?: AbortSignal): Promise<DocumentTree>;
  /** Load a single document by its tree id. */
  loadDocument(id: string, signal?: AbortSignal): Promise<Document>;
}
